import * as React from 'react'
import styled, { css } from 'styled-components'

import { Button } from '../Button'
import { Icon } from '../Icon'
import { Typography } from '../Typography'

export type TablePaginationProps = React.ComponentPropsWithoutRef<'div'> & {
  totalCount: number
  noTotalCount?: boolean
  fromCount: number
  toCount: number
  nextDisabled?: boolean
  onNextClick?(): void
  onPrevClick?(): void
  prevDisabled?: boolean
  loading?: string | boolean | React.ReactNode
}

const LoadingIcon = styled(Icon)`
  animation: spin 1s infinite linear;
  margin-right: 5px;
  position: relative;
  top: 5px;
  path {
    fill: var(--text-secondary);
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
  @prefers-reduced-motion: reduce {
    animation: none;
  }
`

const TablePagination = ({
  fromCount,
  toCount,
  totalCount,
  noTotalCount,
  nextDisabled,
  prevDisabled,
  onPrevClick,
  onNextClick,
  loading,
  children,
  ...rest
}: TablePaginationProps) => {
  const pageSize = fromCount - toCount
  const currentPage = Math.ceil(fromCount / pageSize)

  return (
    <nav
      aria-label='Pagination'
      css={`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-block: 15px;
      `}
      {...rest}
    >
      <div>{children}</div>
      <div css='display: inline-flex; align-items: center; gap: 8px;'>
        <Button
          size='medium'
          disabled={prevDisabled}
          onClick={(e) => {
            e.preventDefault()
            onPrevClick?.()
          }}
          round
          aria-label='Previous'
          icon='CHEVRON_LEFT'
        />
        <div
          css={css`
          display: flex;
          flex-direction: row;
          gap: 4px;
          justify-content: space-between;
          align-items: center;
          `}
        >
          <Typography variant='body-sm' data-testid='table-pag-text'>
            {toCount === 1 || totalCount === 1 ? (
              <>
                Showing <b>only</b> result
              </>
            ) : totalCount ? (
              <>
                {noTotalCount ? (
                  <>
                    Showing <b>{fromCount}</b> to <b>{toCount}</b> of&nbsp;
                    {totalCount > pageSize ? (
                      <b>many</b>
                    ) : (
                      <>
                        <b>{totalCount}</b> results
                      </>
                    )}
                  </>
                ) : (
                  <>
                    Showing <b>{fromCount}</b> to{' '}
                    <b>{Math.min(toCount, totalCount)}</b> of&nbsp;
                    <b>{totalCount}</b> results
                  </>
                )}
              </>
            ) : (
              'No results'
            )}
          </Typography>
          {loading && <LoadingIcon />}
        </div>
        <Button
          size='medium'
          disabled={nextDisabled}
          onClick={(e) => {
            e.preventDefault()
            onNextClick?.()
          }}
          round
          aria-label='Next'
          icon='CHEVRON_RIGHT'
        />
      </div>
    </nav>
  )
}

export default TablePagination
